import { VerticalLayout } from '@realtyfeed/app-shell';
import { signOutUser, useAuthSessionStore } from '@realtyfeed/auth';
import { ROUTES } from '@realtyfeed/routes';
import { Typography } from '@realtyfeed/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigation } from '../config/navigation';

/**
 * The wordmark in the sidebar header.
 *
 * Text rather than the legacy's `require()`d SVG pair: the legacy carried a light and a dark
 * image because its theme could not recolour one, and neither asset came into
 * `shared/assets`. When a real mark is supplied it replaces this; until then a styled wordmark
 * beats a broken `<img>`.
 */
const Brand = (): ReactNode => (
  <Typography as="span" size="lg" weight="bold" className="text-ds-text-primary">
    RF <span className="text-ds-text-accent">Backend</span>
  </Typography>
);

/**
 * The signed-in chrome, wired to this product's nav, profile route and sign-out.
 *
 * `VerticalLayout` takes all of that as props and decides none of it — see its own note. This
 * component is the only place that knows which nav tree and which routes belong to RF Backend.
 */
export const AppLayout = (): ReactNode => {
  const claims = useAuthSessionStore((state) => state.claims);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const name =
    [claims?.given_name, claims?.family_name].filter(Boolean).join(' ') ||
    claims?.['cognito:username'] ||
    claims?.email ||
    'Staff';

  const handleSignOut = () => {
    // Navigate first: signing out flips the session to unauthenticated, and a PrivateRoute
    // would otherwise bounce to the sign-in page with a `from` pointing at wherever the user
    // happened to be — so the next sign-in would drop them back on a page they just left.
    navigate(ROUTES.signIn, { replace: true });
    // Server state belongs to the account that fetched it; the next sign-in must not see it.
    queryClient.clear();
    void signOutUser();
  };

  return (
    <VerticalLayout
      navigation={navigation}
      brand={<Brand />}
      user={{ name, email: claims?.email ?? null }}
      onSignOut={handleSignOut}
      profilePath={ROUTES.profile}
    />
  );
};
